import { CallState } from '../../../features/call/call.types';
import { type CallService } from '../interface';
import {
  createIssueProcessor,
  type IssueProcessor,
  type IssuesObserverPayload,
} from './utils/issueProcessor';
import { type Logger } from 'loglevel';
import {
  createNetworkScoresObservable,
  type ProcessedNetworkScores,
} from './utils/networkScoresObservable';
import { BehaviorSubject, type Subscription } from 'rxjs';
import { session } from '../../../webrtc/session';
import { AutoVideoQualityAdjuster } from './utils/autoVideoQualityAdjuster';
import { type ServiceTokens } from '../../../container';
import { getGrowthBookConfigLoader } from '@doxyme/config';
import { getIssueDetector } from './utils/issueDetector';
import { QOS_STATS_INTERVAL } from './constants';

type QualityOfServiceDependencies = {
  [ServiceTokens.Call]: CallService;
  [ServiceTokens.Logger]: Logger;
  [ServiceTokens.GrowthBook]: ReturnType<typeof getGrowthBookConfigLoader>;
};

export class QualityOfService {
  private readonly issueDetector = getIssueDetector();

  private readonly logger: Logger;

  private hasStarted = false;

  private networkScoresSubscription: Subscription | null = null;

  private readonly issueProcessor: IssueProcessor | null = null;

  private issuesSubscription?: Subscription;

  private isReconnecting = false;

  private call: CallService;

  public readonly networkQuality = new BehaviorSubject<ProcessedNetworkScores>({
    inbound: undefined,
    outbound: undefined,
    outboundNetworkQuality: undefined,
    inboundNetworkQuality: undefined,
  });

  private readonly autoVideoQualityAdjuster?: AutoVideoQualityAdjuster;

  constructor({ call, logger }: QualityOfServiceDependencies) {
    this.logger = logger;

    this.issueProcessor = createIssueProcessor(
      this.issueDetector.eventEmitter,
      QOS_STATS_INTERVAL,
    );

    this.autoVideoQualityAdjuster = new AutoVideoQualityAdjuster();

    this.call = call;

    call.state.subscribe((state) => {
      if (state.matches(CallState.Initializing) && !this.hasStarted) {
        this.start();
      } else if (state.matches({ [CallState.InProgress]: 'reconnecting' })) {
        this.isReconnecting = true;
        this.networkQuality.next({
          inbound: undefined,
          outbound: 1,
          outboundNetworkQuality: undefined,
          inboundNetworkQuality: undefined,
        });
      } else if (state.matches({ [CallState.InProgress]: 'running' })) {
        if (this.isReconnecting) {
          this.networkQuality.next({
            inbound: undefined,
            outbound: undefined,
            outboundNetworkQuality: undefined,
            inboundNetworkQuality: undefined,
          });
        }
        this.isReconnecting = false;
      } else if (
        state.matches(CallState.Leaving) ||
        (state.matches(CallState.LeavingAfterError) && this.hasStarted) ||
        (state.matches(CallState.Idle) && this.hasStarted)
      ) {
        this.stop();
      }
    });
  }

  private start(): void {
    this.hasStarted = true;
    this.logger.log('Start monitoring');

    this.setupNetworkScoresObservable();

    this.issueProcessor?.start();

    this.issuesSubscription = this.issueProcessor?.issues.subscribe(
      this.onIssue.bind(this),
    );
    this.issueDetector.watchNewPeerConnections();

    this.autoVideoQualityAdjuster?.start(this.call);
  }

  private onIssue(value: IssuesObserverPayload): void {
    for (const issue of value.new) {
      this.logger.warn(`newIssue ${issue.type} ${issue.reason}`, {
        issue,
      });
    }
    for (const issue of value.ended) {
      this.logger.warn(`endedIssue ${issue.type} ${issue.reason}`, {
        issue,
      });
    }
  }

  private stop(): void {
    if (!this.hasStarted) {
      return;
    }

    this.hasStarted = false;

    this.issueDetector.stopWatchingNewPeerConnections();
    this.issueDetector.eventEmitter.removeAllListeners();
    // unsubscribe from observables
    this.networkScoresSubscription?.unsubscribe();
    this.networkScoresSubscription = null;
    this.issuesSubscription?.unsubscribe();
    this.issuesSubscription = undefined;

    this.issueProcessor?.stop();
    // log summary of issues
    this.logger.log('callIssueSummary', {
      issuesSummary: this.issueProcessor?.getIssueSummary(),
    });
    this.issueProcessor?.reset();

    this.networkQuality.next({
      inbound: undefined,
      outbound: undefined,
      inboundNetworkQuality: undefined,
      outboundNetworkQuality: undefined,
    });

    this.autoVideoQualityAdjuster?.stop();
  }

  private setupNetworkScoresObservable(): void {
    // This code us useful for the network indicator
    const processedScores = createNetworkScoresObservable(
      this.issueDetector.eventEmitter,
      QOS_STATS_INTERVAL,
    );
    this.networkScoresSubscription = processedScores.subscribe(
      (result: ProcessedNetworkScores) => {
        const outbound = this.isReconnecting ? 1 : result.outbound; // set to 1 if reconnecting as the network quality is not reliable
        this.networkQuality.next({
          inbound: result.inbound,
          inboundNetworkQuality: result.inboundNetworkQuality,
          outbound,
          outboundNetworkQuality: result.outboundNetworkQuality,
        });
        this.logger.log('Network scores:', result);

        session.updateNetworkQualityForAllParticipants(result);
      },
    );
  }

  dispose(): void {
    this.stop();
  }
}
